import { createServerFn } from "@tanstack/react-start"
import { z } from "zod"
import { requireInventoryUserId } from "@/inventory/lib/auth.server"
import { buildSupplyItemsCsv } from "../lib/export-csv"
import { buildSupplyItemsExcel } from "../lib/export-excel"
import { buildSupplyItemsPdfSummary } from "../lib/export-pdf"
import {
  describeSupplyFilters,
  fetchSupplyExportRows,
} from "../lib/export-rows"

const exportFiltersSchema = z
  .object({
    categoryId: z.number().optional(),
    isActive: z.boolean().nullable().optional(),
    search: z.string().optional(),
    lowStockOnly: z.boolean().optional(),
    filtersDescription: z.string().optional(),
  })
  .optional()

export type ExportPayload = {
  filename: string
  contentType: string
  base64: string
}

function toPayload(
  filename: string,
  contentType: string,
  buffer: ArrayBuffer,
): ExportPayload {
  const bytes = new Uint8Array(buffer)
  let binary = ""
  for (const b of bytes) binary += String.fromCharCode(b)
  return {
    filename,
    contentType,
    base64: btoa(binary),
  }
}

function stamp(): string {
  return new Date().toISOString().slice(0, 19).replaceAll(":", "-")
}

export const exportSupplyItemsCsvFn = createServerFn({ method: "GET" })
  .validator((data?: unknown) => exportFiltersSchema.parse(data ?? {}))
  .handler(async ({ data }) => {
    await requireInventoryUserId()
    const filters = data ?? {}
    const rows = await fetchSupplyExportRows(filters)
    const csv = buildSupplyItemsCsv(rows)
    const encoded = new TextEncoder().encode(csv)
    const buffer = encoded.buffer.slice(
      encoded.byteOffset,
      encoded.byteOffset + encoded.byteLength,
    ) as ArrayBuffer
    return toPayload(
      `supply-items-${stamp()}.csv`,
      "text/csv;charset=utf-8",
      buffer,
    )
  })

export const exportSupplyItemsExcelFn = createServerFn({ method: "GET" })
  .validator((data?: unknown) => exportFiltersSchema.parse(data ?? {}))
  .handler(async ({ data }) => {
    await requireInventoryUserId()
    const filters = data ?? {}
    const rows = await fetchSupplyExportRows(filters)
    const buffer = buildSupplyItemsExcel(rows)
    return toPayload(
      `supply-items-${stamp()}.xlsx`,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      buffer,
    )
  })

export const exportSupplyItemsPdfSummaryFn = createServerFn({ method: "GET" })
  .validator((data?: unknown) => exportFiltersSchema.parse(data ?? {}))
  .handler(async ({ data }) => {
    await requireInventoryUserId()
    const filters = data ?? {}
    const rows = await fetchSupplyExportRows(filters)
    const generatedAt = Date.now()
    const filtersDescription =
      filters.filtersDescription?.trim() || describeSupplyFilters(filters)
    const buffer = await buildSupplyItemsPdfSummary(rows, {
      generatedAt,
      filtersDescription,
    })
    return toPayload(`supply-summary-${stamp()}.pdf`, "application/pdf", buffer)
  })
